var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { FieldWrapper, FormField, FormGrid, Modal, ModalBody, ModalFoot, ModalHead, } from '@dixa/lib-shared-habla';
import produce from 'immer';
import noop from 'lodash/noop';
import omit from 'lodash/omit';
import { useForm, useWatch } from 'react-hook-form';
import { searchApi } from 'apis/search';
import { analytics } from 'app/utils/segment';
import { HookFormText } from 'components/hookForm';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { createAppModal } from 'utils/modal';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { searchActions } from '../../../search.actions';
import { SavedSearchFilters } from '../filters/savedSearchFilters';
import styles from './savedSearchFormModal.module.scss';
const formId = 'saved-search-form';
export const SavedSearchFormModal = createAppModal({ type: 'search:savedSearch:form' }, ({ isOpen, modal, hide }) => {
    const dispatch = useAppDispatch();
    const notify = useNotifications();
    const { params, savedSearch: activeSavedSearch } = useAppSelector((state) => state.search);
    const { mode = 'create' } = modal.arg;
    const [createSavedSearch, createState] = searchApi.useCreateSavedSearchMutation();
    const [updateSavedSearch, updateState] = searchApi.useUpdateSavedSearchMutation();
    const form = useForm({
        mode: 'all',
        defaultValues: {
            name: (mode === 'update' && (activeSavedSearch === null || activeSavedSearch === void 0 ? void 0 : activeSavedSearch.name)) || '',
            params: Object.assign(Object.assign({}, activeSavedSearch === null || activeSavedSearch === void 0 ? void 0 : activeSavedSearch.params), omit(params, 'page')),
        },
    });
    const paramsValue = useWatch({ control: form.control, name: 'params' });
    const handleCreateSavedSearch = (values) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const { data: { saved_search }, } = yield createSavedSearch(values).unwrap();
            analytics.savedSearchCreated({
                saveAsNew: !!activeSavedSearch,
            });
            notify({
                title: 'Saved search created',
                text: `"${values.name}" has been created.`,
                className: 'success',
                icon: 'i-done',
                time: 5000,
            }, NotificationType.Success);
            return saved_search;
        }
        catch (error) {
            notify({
                title: 'An error occurred',
                text: `An error occurred when trying to create the "${values.name}" saved search.`,
            }, NotificationType.Error);
        }
    });
    const handleUpdateSavedSearch = (values) => __awaiter(void 0, void 0, void 0, function* () {
        if (!activeSavedSearch)
            return;
        try {
            const { data: { saved_search }, } = yield updateSavedSearch({
                saved_search: Object.assign(Object.assign({}, activeSavedSearch), values),
            }).unwrap();
            notify({
                title: 'Saved search update',
                text: `"${values.name}" has been updated.`,
                className: 'success',
                icon: 'i-done',
                time: 5000,
            }, NotificationType.Success);
            return saved_search;
        }
        catch (error) {
            notify({
                title: 'An error occurred',
                text: `An error occurred when trying to update the "${values.name}" saved search.`,
            }, NotificationType.Error);
        }
    });
    const handleValidSubmit = (values) => __awaiter(void 0, void 0, void 0, function* () {
        const savedSearch = mode === 'update'
            ? yield handleUpdateSavedSearch(values)
            : yield handleCreateSavedSearch(values);
        if (savedSearch) {
            dispatch(searchActions.savedSearch.apply(savedSearch));
        }
        hide === null || hide === void 0 ? void 0 : hide();
    });
    const handleRemove = (field) => {
        const nextParams = produce(paramsValue, (draft) => {
            if (field === 'query') {
                draft.query = '';
                draft.strict = false;
            }
            else {
                const fields = Array.isArray(field) ? field : [field];
                fields.forEach((f) => {
                    var _a, _b;
                    const conditionIndex = (_a = draft.filters) === null || _a === void 0 ? void 0 : _a.conditions.findIndex(({ field }) => field === f);
                    if (conditionIndex !== undefined && conditionIndex !== -1) {
                        (_b = draft.filters) === null || _b === void 0 ? void 0 : _b.conditions.splice(conditionIndex, 1);
                    }
                });
            }
        });
        form.setValue('params', nextParams, { shouldDirty: true });
    };
    return (_jsxs(Modal, Object.assign({ isOpen: isOpen, onClose: hide, className: styles.modal }, { children: [_jsx(ModalHead, Object.assign({ title: mode === 'update' ? 'Update saved search' : 'Create saved search', onClose: hide }, { children: mode === 'create' &&
                    'Saving a search makes it easy to come back to where you left off. All you have to do is give it a name.' })), _jsx(ModalBody, { children: _jsx("form", Object.assign({ id: formId, noValidate: true, tabIndex: -1, onSubmit: form.handleSubmit(handleValidSubmit) }, { children: _jsxs(FormGrid, { children: [_jsx(FormField, { children: _jsx(HookFormText, { name: "name", label: "Name", control: form.control, rules: {
                                        required: 'Name is required',
                                    }, componentProps: {
                                        autoFocus: true,
                                        placeholder: 'e.g. Assigned to me',
                                    } }) }), _jsx(FormField, { children: _jsx(FieldWrapper, Object.assign({ labelFor: "filters", label: "Filters applied" }, { children: _jsx(SavedSearchFilters, { variant: "tags", params: paramsValue, onRemove: handleRemove }) })) })] }) })) }), _jsx(ModalFoot, { confirmName: mode === 'update' ? 'Update search' : 'Save search', cancelName: "Cancel", confirmOnClick: noop, cancelOnClick: hide, confirmProps: {
                    type: 'submit',
                    isLoading: createState.isLoading || updateState.isLoading,
                    disabled: !form.formState.isValid || form.formState.isSubmitting,
                    form: formId,
                } })] })));
});
